class ProductBundleUpsell extends HTMLElement {
  constructor() {
    super();
    this.tier = 1;
    this.selections = {}; // slotIndex -> { variantId, title, price, image, productTitle }
    this.activeSlotIndex = null;
  }

  connectedCallback() {
    this.slotsEl = this.querySelector('[data-slots]');
    this.slotTemplate = this.querySelector('[data-slot-template]');
    this.ctaBtn = this.querySelector('[data-cta]');
    this.ctaLabel = this.querySelector('[data-cta-label]');
    this.errorEl = this.querySelector('[data-error]');
    this.modal = this.querySelector('[data-modal]');
    this.styleGrid = this.querySelector('[data-style-grid]');
    this.sizePicker = this.querySelector('[data-size-picker]');
    this.sizeOptionsEl = this.querySelector('[data-size-options]');
    this.sizeConfirmBtn = this.querySelector('[data-size-confirm]');

    // Move the modal to <body> so it isn't trapped by any ancestor with a
    // CSS transform (common with theme entrance-animation wrappers), which
    // breaks position:fixed and shrinks the overlay into a small box.
    // IMPORTANT: this must happen AFTER the querySelector calls above,
    // since those look for elements *inside* the modal — moving it first
    // would leave those elements outside `this` and return null.
    if (this.modal && this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
    }

    try {
      this.products = JSON.parse(this.querySelector('[data-bundle-products]').textContent);
    } catch (e) {
      this.products = [];
    }

    this.querySelectorAll('.pbu__tier').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setTier(parseInt(btn.dataset.tier, 10));
      });
    });

    this.querySelector('.pbu__head-toggle').addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.classList.toggle('is-collapsed');
    });

    this.slotsEl.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-choose-style]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      const slot = trigger.closest('.pbu__slot');
      this.openStylePicker(parseInt(slot.dataset.slotIndex, 10));
    });

    this.modal.querySelectorAll('[data-modal-close]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      });
    });

    this.styleGrid.addEventListener('click', (e) => {
      const card = e.target.closest('.pbu-style-card');
      if (!card) return;
      e.preventDefault();
      e.stopPropagation();
      this.openSizeStep(card.dataset.productHandle);
    });

    this.modal.querySelector('[data-size-back]').addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.sizePicker.hidden = true;
      this.styleGrid.hidden = false;
    });

    this.sizeConfirmBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.confirmSize();
    });

    this.ctaBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.addBundleToCart();
    });

    this.setTier(1);
  }

  setTier(tier) {
    this.tier = tier;
    this.querySelectorAll('.pbu__tier').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(parseInt(btn.dataset.tier, 10) === tier));
    });

    // Slot 0 is always the locked "this item" slot. Build slots 1..tier-1.
    this.slotsEl.querySelectorAll('.pbu__slot:not([data-locked])').forEach((el) => el.remove());

    for (let i = 1; i < tier; i++) {
      const node = this.slotTemplate.content.firstElementChild.cloneNode(true);
      node.dataset.slotIndex = String(i);
      if (this.selections[i]) this.fillSlotNode(node, this.selections[i]);
      this.slotsEl.appendChild(node);
    }

    // Drop selections beyond the current tier so they don't get added silently.
    Object.keys(this.selections).forEach((k) => {
      if (parseInt(k, 10) >= tier) delete this.selections[k];
    });

    this.updateCta();
  }

  fillSlotNode(node, selection) {
    node.classList.add('is-filled');
    node.querySelector('.pbu__slot-thumb').innerHTML =
      '<img src="' + selection.image + '" alt="" width="50" height="50">';
    node.querySelector('.pbu__slot-thumb').classList.remove('pbu__slot-thumb--empty');
    node.querySelector('.pbu__slot-name').textContent = selection.productTitle;
    node.querySelector('.pbu__slot-sub').textContent = selection.title;
    const actionBtn = node.querySelector('[data-choose-style]');
    actionBtn.textContent = 'Change';
  }

  openStylePicker(slotIndex) {
    this.activeSlotIndex = slotIndex;
    this.sizePicker.hidden = true;
    this.styleGrid.hidden = false;
    this.modal.querySelector('[data-modal-title]').textContent = 'Choose a style';
    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.modal.hidden = true;
    document.body.style.overflow = '';
  }

 openSizeStep(handle) {
  const product = this.products.find((p) => p.handle === handle);

  if (!product) {
    console.error('Product not found:', handle);
    return;
  }

  this.activeProduct = product;

  this.modal.querySelector('[data-modal-title]').textContent = product.title;

  const productImg = this.modal.querySelector('[data-size-product-img]');
  const productName = this.modal.querySelector('[data-size-product-name]');
  const productPrice = this.modal.querySelector('[data-size-product-price]');

  productImg.src = product.image || '';
  productImg.alt = product.title;

  productName.textContent = product.title;
  productPrice.textContent = product.price_formatted || '';

  this.sizeOptionsEl.innerHTML = '';

  this.selectedVariant = null;

  /* Reset button */
  this.sizeConfirmBtn.disabled = true;
  this.sizeConfirmBtn.textContent = 'Select a size';

  /* SINGLE VARIANT */
  if (product.variants.length === 1) {

    const variant = product.variants[0];

    this.selectedVariant = variant;

    this.sizeConfirmBtn.disabled = !variant.available;

    this.sizeConfirmBtn.textContent = variant.available
      ? 'Add this pair'
      : 'Out of stock';

  } else {

    product.variants.forEach((variant) => {

      const chip = document.createElement('button');

      chip.type = 'button';
      chip.className = 'pbu-size-chip';

      chip.textContent = variant.option || variant.title;

      chip.disabled = !variant.available;

      chip.setAttribute('aria-pressed', 'false');

      chip.addEventListener('click', (e) => {

        e.preventDefault();
        e.stopPropagation();

        /* Remove selection from all sizes */
        this.sizeOptionsEl
          .querySelectorAll('.pbu-size-chip')
          .forEach((button) => {
            button.setAttribute('aria-pressed', 'false');
          });

        /* Select current size */
        chip.setAttribute('aria-pressed', 'true');

        /* Save selected variant */
        this.selectedVariant = variant;

        /* Enable button */
        this.sizeConfirmBtn.disabled = false;

        /* Change button text */
        this.sizeConfirmBtn.textContent = 'Add this pair';

      });

      this.sizeOptionsEl.appendChild(chip);

    });

  }

  /* IMPORTANT: Switch views */
  this.styleGrid.hidden = true;

  this.sizePicker.hidden = false;

  /* Force button visibility */
  this.sizeConfirmBtn.style.display = 'flex';
}

  confirmSize() {
    if (!this.selectedVariant || this.activeSlotIndex === null) return;
    const selection = {
      variantId: this.selectedVariant.id,
      title: this.selectedVariant.title,
      price: this.selectedVariant.price,
      image: this.selectedVariant.image || this.activeProduct.image,
      productTitle: this.activeProduct.title,
    };
    this.selections[this.activeSlotIndex] = selection;

    const slotNode = this.slotsEl.querySelector('[data-slot-index="' + this.activeSlotIndex + '"]');
    if (slotNode) this.fillSlotNode(slotNode, selection);

    this.closeModal();
    this.updateCta();
  }

  updateCta() {
    const requiredExtras = this.tier - 1;
    const filled = Object.keys(this.selections).length;
    const remaining = requiredExtras - filled;

    if (this.tier === 1) {
      this.ctaLabel.textContent = 'Add to cart';
      this.ctaBtn.disabled = false;
    } else if (remaining > 0) {
      this.ctaLabel.textContent = 'Add ' + remaining + ' more pair' + (remaining > 1 ? 's' : '');
      this.ctaBtn.disabled = true;
    } else {
      this.ctaLabel.textContent = 'Add ' + this.tier + ' pairs to cart';
      this.ctaBtn.disabled = false;
    }
    this.errorEl.hidden = true;
  }

  async addBundleToCart() {
    const items = [{ id: parseInt(this.dataset.variantId, 10), quantity: 1 }];
    Object.values(this.selections).forEach((s) => items.push({ id: parseInt(s.variantId, 10), quantity: 1 }));

    this.ctaBtn.disabled = true;
    const originalLabel = this.ctaLabel.textContent;
    this.ctaLabel.textContent = 'Adding…';

    try {
      const res = await fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add items to cart');
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      window.location.href = '/cart';
    } catch (err) {
      this.errorEl.textContent = err.message || 'Something went wrong. Please try again.';
      this.errorEl.hidden = false;
      this.ctaLabel.textContent = originalLabel;
      this.ctaBtn.disabled = false;
    }
  }
}

customElements.define('product-bundle-upsell', ProductBundleUpsell);